/**
 * SB2-W-16 — Projects list page (`/projects`).
 *
 * Net-new page promoted from
 * `.feedback/26-05-20 AFTER SB1 IMPLEMENTATION/03-page_projects.md`.
 * Uses the canonical `DataPageShell` template per
 * `.vibe/60_TEMPLATE_DATA_PAGE.md`.
 *
 * Filter order (locked, top-to-bottom in the toolbar):
 *   1. Search field (project_code, project_name, customer name, contractor name)
 *   2. Customer multiselect
 *   3. Contractor multiselect
 *   4. Show completed toggle (default OFF)
 *
 * Column order locked in `columns.tsx`.
 *
 * "Add project" navigates to the dedicated full-page create flow at
 * `/projects/create` (PM-4, PM-D20); the pencil (Edit) deep-links to the
 * project detail page (`/projects/:projectId`). This page implements
 * neither create nor edit inline — both are dedicated pages.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import type { GridRowSelectionModel, GridSortModel } from "@mui/x-data-grid";
import {
  DataPageFooter,
  DataPageShell,
  DataPageToolbar,
  DataTable,
  SearchField,
  TypeaheadChipFilter,
  sidebar as sidebarTokens,
  typography,
  useCanEditResourceFor,
} from "@aeriosiq/ui";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ToastProvider } from "../account/ToastContext.js";
import {
  useProjectsContractorFacetQuery,
  useProjectsCustomerFacetQuery,
} from "./useProjects.js";
import { useOwnerMemberList } from "./access/useOwnerMemberList.js";
import { toCustomerProjectRow } from "./access/customerProjection.js";
import { isCustomerRole, WORKFLOW_UNSET } from "./types.js";
import {
  BulkSetWorkflowDialog,
  MAX_BULK_WORKFLOW,
} from "./BulkSetWorkflowDialog.js";
import type {
  ListProjectsParams,
  ProjectListItem,
  ProjectsSortDir,
  ProjectsSortKey,
  ProjectState,
} from "./types.js";
import { PROJECT_STATES, PROJECTS_SORT_KEYS } from "./types.js";

// SB4-05 / F-05 — state multi-select options for the TypeaheadChipFilter.
// 10-value enum, fixed at module load. Display labels are derived from
// the enum key (Title Case + replace underscores) so the dropdown reads
// naturally without requiring a separate facet endpoint.
const STATE_OPTIONS: readonly { id: ProjectState; name: string }[] =
  PROJECT_STATES.map((s) => ({
    id: s,
    name: s
      .toLowerCase()
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" "),
  }));
import {
  buildProjectColumns,
  buildCustomerProjectColumns,
  ownershipColumn,
} from "./columns.js";
import { FolderIcon, PlusIcon } from "@aeriosiq/ui/icons";

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_SORT_BY: ProjectsSortKey = "projectCode";
const DEFAULT_SORT_DIR: ProjectsSortDir = "desc";

function parseSort(
  value: string | null,
): { key: ProjectsSortKey; dir: ProjectsSortDir } | null {
  if (!value) return null;
  const [k, d] = value.split(":");
  if (!k || !d) return null;
  if (!(PROJECTS_SORT_KEYS as readonly string[]).includes(k)) return null;
  if (d !== "asc" && d !== "desc") return null;
  return { key: k as ProjectsSortKey, dir: d };
}

function parsePageSize(value: string | null): number {
  const n = Number(value ?? "");
  if (
    !Number.isFinite(n) ||
    !PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])
  ) {
    return DEFAULT_PAGE_SIZE;
  }
  return n;
}

function parseCsv(value: string | null): string[] {
  if (!value) return [];
  const seen = new Set<string>();
  for (const raw of value.split(",")) {
    const v = raw.trim();
    if (v.length > 0) seen.add(v);
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(id);
    };
  }, [value, ms]);
  return debounced;
}

export function ProjectsPage(): ReactElement {
  return (
    <ToastProvider>
      <ProjectsPageInner />
    </ToastProvider>
  );
}

function ProjectsPageInner(): ReactElement {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const includeCompleted = params.get("includeCompleted") === "1";
  // PM-WF-01 — the "Workflow not set" filter (URL-driven, shareable).
  const workflowUnsetOnly = params.get("workflow") === WORKFLOW_UNSET;
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const bulkRanRef = useRef(false);
  const [searchInput, setSearchInput] = useState(params.get("search") ?? "");
  const debouncedSearch = useDebounced(searchInput, 500);

  // SB2-CC-04 / CONS-DEFER-01 — Add button hidden when the matrix
  // denies `project.create`. tenant_admin / project_manager /
  // platform_admin are permitted per the matrix. `project.create` is
  // a CONDITIONAL cell for those roles; the plain `useCanEditResource`
  // would return `false` for the conditional cell and hide the button
  // for every tenant admin / PM ("I'm tenant_admin but the button
  // isn't there" — the class of bug CONS-DEFER-01 closes).
  // `useCanEditResourceFor` resolves the conditional with the
  // same-tenant default: there's no row resource yet (this is the
  // create CTA on the list page), so the predicate falls through to
  // "tenant unknown → assume same-tenant" and the matrix-permitted
  // roles see the button.
  const canCreate = useCanEditResourceFor("project.create");

  const lastAppliedSearchRef = useRef(params.get("search") ?? "");
  useEffect(() => {
    if (lastAppliedSearchRef.current === debouncedSearch) return;
    lastAppliedSearchRef.current = debouncedSearch;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch.length > 0) {
          next.set("search", debouncedSearch);
        } else {
          next.delete("search");
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, setParams]);

  const sortParsed = parseSort(params.get("sort"));
  const pageSize = parsePageSize(params.get("pageSize"));
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);
  const customerSelected = useMemo(
    () => parseCsv(params.get("customer")),
    [params],
  );
  const contractorSelected = useMemo(
    () => parseCsv(params.get("contractor")),
    [params],
  );
  // SB4-05 / F-05 — state multi-select selection, URL-driven. Filter
  // out any token that isn't in the canonical PROJECT_STATES enum so
  // a stale link (e.g. ?state=FINISHED) silently degrades to "no state
  // filter" rather than rendering a Chip for an invalid value.
  const stateSelected = useMemo<ProjectState[]>(() => {
    const raw = parseCsv(params.get("state"));
    return raw.filter((s): s is ProjectState =>
      (PROJECT_STATES as readonly string[]).includes(s),
    );
  }, [params]);

  const queryParams = useMemo<ListProjectsParams>(() => {
    const out: ListProjectsParams = { page, pageSize };
    if (debouncedSearch.length > 0) out.q = debouncedSearch;
    out.sortBy = sortParsed?.key ?? DEFAULT_SORT_BY;
    out.sortDir = sortParsed?.dir ?? DEFAULT_SORT_DIR;
    out.includeCompleted = includeCompleted;
    if (customerSelected.length > 0) out.customerIds = customerSelected;
    if (contractorSelected.length > 0) out.contractorIds = contractorSelected;
    if (stateSelected.length > 0) out.states = stateSelected;
    if (workflowUnsetOnly) out.workflow = WORKFLOW_UNSET;
    return out;
  }, [
    workflowUnsetOnly,
    page,
    pageSize,
    debouncedSearch,
    sortParsed,
    includeCompleted,
    customerSelected,
    contractorSelected,
    stateSelected,
  ]);

  // TNCY-6 §c — fire BOTH scopes (own + member) and merge into one table.
  // The own-only path is unchanged: a tenant with no cross-tenant grants
  // gets the same list as before (no member rows ⇒ own items verbatim).
  const {
    rows: mergedRows,
    total: mergedTotal,
    isPending,
    isError,
    workflowUnsetCount,
  } = useOwnerMemberList(queryParams);
  const { data: customerFacet } = useProjectsCustomerFacetQuery();
  const { data: contractorFacet } = useProjectsContractorFacetQuery();
  const customerOptions = customerFacet?.items ?? [];
  const contractorOptions = contractorFacet?.items ?? [];

  function setCustomerSelected(values: readonly string[]): void {
    const canonical = [...new Set(values.map((c) => c.trim()))]
      .filter((c) => c.length > 0)
      .sort((a, b) => a.localeCompare(b));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (canonical.length === 0) next.delete("customer");
        else next.set("customer", canonical.join(","));
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function setContractorSelected(values: readonly string[]): void {
    const canonical = [...new Set(values.map((c) => c.trim()))]
      .filter((c) => c.length > 0)
      .sort((a, b) => a.localeCompare(b));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (canonical.length === 0) next.delete("contractor");
        else next.set("contractor", canonical.join(","));
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function setStateSelected(values: readonly string[]): void {
    // Canonicalise to PROJECT_STATES set; drop unknowns. Sort for stable
    // URL ordering (so a shared link is reproducible regardless of
    // pick order).
    const canonical = [...new Set(values)]
      .filter((v): v is ProjectState =>
        (PROJECT_STATES as readonly string[]).includes(v),
      )
      .sort((a, b) => a.localeCompare(b));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (canonical.length === 0) next.delete("state");
        else next.set("state", canonical.join(","));
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function toggleWorkflowUnset(checked: boolean): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (checked) next.set("workflow", WORKFLOW_UNSET);
        else next.delete("workflow");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
    setSelection([]);
  }
  function toggleIncludeCompleted(checked: boolean): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (checked) next.set("includeCompleted", "1");
        else next.delete("includeCompleted");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function setPage(p: number): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (p <= 1) next.delete("page");
        else next.set("page", String(p));
        return next;
      },
      { replace: true },
    );
  }
  function setPageSize(n: number): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (n === DEFAULT_PAGE_SIZE) next.delete("pageSize");
        else next.set("pageSize", String(n));
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }

  const sortModel = useMemo<GridSortModel>(() => {
    const key = sortParsed?.key ?? DEFAULT_SORT_BY;
    const dir = sortParsed?.dir ?? DEFAULT_SORT_DIR;
    return [{ field: key, sort: dir }];
  }, [sortParsed]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const head = model[0];
      if (!head?.sort) {
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("sort");
            return next;
          },
          { replace: true },
        );
        return;
      }
      if (!(PROJECTS_SORT_KEYS as readonly string[]).includes(head.field)) {
        return;
      }
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("sort", `${head.field}:${head.sort ?? "asc"}`);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // TNCY-6 §a/b — the pencil opens the dedicated Project detail page, where
  // the access-grant panel + party pickers live. Member (shared-in) rows are
  // read-only — their grants are managed by the owning tenant, so the detail
  // page gates the panel itself.
  const onEdit = useCallback(
    (row: ProjectListItem) => {
      void navigate(`/projects/${row.projectId}`);
    },
    [navigate],
  );

  // PM-4 — "Add project" opens the dedicated full-page create flow
  // (`/projects/create`), replacing the former coming-soon toast (PM-D20).
  const onAdd = useCallback(() => {
    void navigate("/projects/create");
  }, [navigate]);

  // TNCY-6 §d — customer read view. When EVERY visible row is a member
  // project under a customer role (a pure-customer tenant, e.g. an operator
  // who was only ever shared projects as a customer), switch to the outcome-
  // only projection: rows are physically stripped of the chain/commercial
  // fields (AC-4 — absent, not CSS-hidden) and the columns drop Contractor +
  // Edit. A mixed tenant (any own row, or any non-customer member role) keeps
  // the full merged table.
  const isCustomerOnlyView =
    mergedRows.length > 0 &&
    mergedRows.every((r) => r.ownership === "member" && isCustomerRole(r.role));

  const total = mergedTotal;

  const columns = useMemo(() => {
    if (isCustomerOnlyView) {
      // No ownership column needed — every row is a member row; the customer
      // outcome surface stands alone.
      return buildCustomerProjectColumns();
    }
    const base = buildProjectColumns({ onEdit });
    // R0627-05 — always prepend the origin column so the house icon is a
    // consistent provenance tell on own rows too (matching Inspection
    // Instructions), not only when cross-tenant member rows are present.
    return [ownershipColumn<ProjectListItem>(), ...base];
  }, [isCustomerOnlyView, onEdit]);

  // Both branches expose `projectId`; the table maps it to `id`. Typed as a
  // shared minimal shape so `.map` resolves without a union-of-arrays error.
  const rows: { projectId: string }[] = useMemo(
    () =>
      isCustomerOnlyView ? mergedRows.map(toCustomerProjectRow) : mergedRows,
    [isCustomerOnlyView, mergedRows],
  );

  // PM-WF-01 — bulk "Set workflow…": own rows only (a member project's
  // workflow is its owner's), for the project-management roles
  // (`project.create`, the gate the PATCH enforces). Client-side fan-out.
  const canSetWorkflow = canCreate && !isCustomerOnlyView;
  const selectedIds = selection.map((id) => String(id));
  const selectedProjects = useMemo(() => {
    const byId = new Map(mergedRows.map((r) => [r.projectId, r]));
    return selectedIds
      .map((id) => byId.get(id))
      .filter((r): r is ProjectListItem => r !== undefined)
      .map((r) => ({ projectId: r.projectId, projectName: r.projectName }));
  }, [selectedIds, mergedRows]);

  return (
    <DataPageShell title="Projects">
      <DataPageToolbar
        comfortableFilters
        ariaLabel="Projects toolbar"
        filtersTestId="projects-toolbar-filters"
        settingsTestId="projects-toolbar-settings"
        filters={
          <>
            <SearchField
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Search"
              ariaLabel="Search projects"
            />
            <TypeaheadChipFilter
              label="Customer"
              value={customerSelected}
              onChange={setCustomerSelected}
              options={customerOptions}
              getOptionId={(o) => o.id}
              getOptionLabel={(o) => o.name}
            />
            <TypeaheadChipFilter
              label="Contractor"
              value={contractorSelected}
              onChange={setContractorSelected}
              options={contractorOptions}
              getOptionId={(o) => o.id}
              getOptionLabel={(o) => o.name}
            />
            <TypeaheadChipFilter
              label="State"
              value={stateSelected}
              onChange={setStateSelected}
              options={STATE_OPTIONS}
              getOptionId={(o) => o.id}
              getOptionLabel={(o) => o.name}
            />
            {!isCustomerOnlyView ? (
              <FormControlLabel
                control={
                  <Switch
                    checked={workflowUnsetOnly}
                    onChange={(_e, c) => {
                      toggleWorkflowUnset(c);
                    }}
                    inputProps={{
                      "aria-label": "Only projects without a workflow",
                    }}
                  />
                }
                label={
                  workflowUnsetCount !== undefined
                    ? `Workflow not set (${String(workflowUnsetCount)})`
                    : "Workflow not set"
                }
                sx={{ ml: 0, mr: 0 }}
              />
            ) : null}
            <FormControlLabel
              control={
                <Switch
                  checked={includeCompleted}
                  onChange={(_e, c) => {
                    toggleIncludeCompleted(c);
                  }}
                  inputProps={{ "aria-label": "Show completed projects" }}
                />
              }
              label="Show completed"
              sx={{ ml: 0, mr: 0 }}
            />
          </>
        }
        settings={
          <>
            {canSetWorkflow && selectedProjects.length > 0 ? (
              <Button
                aria-label="Set workflow"
                onClick={() => {
                  setBulkOpen(true);
                }}
                size="small"
                sx={{ textTransform: "none" }}
                title={
                  selectedProjects.length > MAX_BULK_WORKFLOW
                    ? `At most ${String(MAX_BULK_WORKFLOW)} projects per action`
                    : undefined
                }
              >
                Set workflow… ({String(selectedProjects.length)})
              </Button>
            ) : null}
            <Box sx={{ flex: 1 }} />
            {canCreate ? (
              <Button
                aria-label="Add project"
                onClick={onAdd}
                startIcon={<PlusIcon fontSize="small" />}
                disableElevation
                sx={(t) => {
                  const mode = t.palette.mode;
                  const tokens =
                    mode === "dark" ? sidebarTokens.dark : sidebarTokens.light;
                  return {
                    height: 32,
                    minHeight: 32,
                    paddingInline: 10,
                    borderRadius: 1.5,
                    fontSize: typography.size["body-md"],
                    fontWeight: typography.weight.medium,
                    letterSpacing: typography.tracking["body-md"],
                    lineHeight: 1,
                    bgcolor: tokens.selectedBg,
                    color: tokens.selectedFg,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: t.palette.primary.light,
                      boxShadow: "none",
                    },
                    "&:active": {
                      bgcolor: tokens.rowPressedBg,
                      color: tokens.selectedFg,
                      boxShadow: "none",
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${t.palette.primary.main}`,
                      outlineOffset: 2,
                    },
                    "& .MuiButton-startIcon": {
                      marginRight: 1,
                      marginLeft: -0.25,
                    },
                  };
                }}
              >
                Add project
              </Button>
            ) : null}
          </>
        }
      />

      {isPending ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            py: 6,
          }}
        >
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error" sx={{ p: 2 }}>
          Couldn&apos;t load projects.
        </Typography>
      ) : rows.length === 0 ? (
        <ProjectsEmptyState onAdd={canCreate ? onAdd : null} />
      ) : (
        <DataTable<ProjectListItem & { id: string }>
          rows={
            rows.map((r) => ({ ...r, id: r.projectId })) as (ProjectListItem & {
              id: string;
            })[]
          }
          columns={columns as never}
          ariaLabel="Projects"
          getRowId={(row) => row.id}
          rowCount={total}
          paginationModel={{ page: page - 1, pageSize }}
          onPaginationModelChange={(m) => {
            const nextPage = m.page + 1;
            if (m.pageSize !== pageSize) {
              setPageSize(m.pageSize);
            } else if (nextPage !== page) {
              setPage(nextPage);
            }
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          pinnedActionColumn
          // PM-WF-01 added the Workflow + selection columns: keep every column
          // in the DOM so the trailing Edit action stays reachable when the
          // row is wider than the page (the IntakesPage precedent).
          columnBufferPx={4000}
          {...(canSetWorkflow
            ? {
                checkboxSelection: true,
                keepNonExistentRowsSelected: true,
                rowSelectionModel: selection,
                onRowSelectionModelChange: (model: GridRowSelectionModel) => {
                  setSelection(model);
                },
                // Own rows only — a member project's workflow is its owner's.
                isRowSelectable: (p: { row: ProjectListItem }) =>
                  p.row.ownership !== "member",
              }
            : {})}
        />
      )}

      <DataPageFooter
        total={total}
        entityLabel="projects"
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        totalTestId="projects-footer-total"
      />

      <BulkSetWorkflowDialog
        open={bulkOpen}
        onClose={() => {
          setBulkOpen(false);
          // After a run the selection has served its purpose; a plain Cancel
          // keeps it.
          if (bulkRanRef.current) {
            bulkRanRef.current = false;
            setSelection([]);
          }
        }}
        projects={selectedProjects}
        onFinished={() => {
          bulkRanRef.current = true;
        }}
      />
    </DataPageShell>
  );
}

function ProjectsEmptyState({
  onAdd,
}: {
  onAdd: (() => void) | null;
}): ReactElement {
  return (
    <Box
      sx={{
        flex: 1,
        py: 8,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      <FolderIcon sx={{ fontSize: 48, color: "text.secondary" }} aria-hidden />
      <Typography variant="h6" color="text.primary">
        No projects yet
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Projects appear here once they are created for your tenant.
      </Typography>
      {onAdd ? (
        <Button variant="contained" onClick={onAdd}>
          Add project
        </Button>
      ) : null}
    </Box>
  );
}
